/**
 * cart-upsell.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Manages the upsell / bestseller row inside the cart drawer.
 *
 * Design principles:
 *  • Zero inline JS in Liquid — this module owns all upsell interactivity.
 *  • Optimistic UI — button state changes BEFORE the fetch completes.
 *  • Surgical DOM update — on success, the upsell card fades out and the
 *    theme's global CartAddEvent is dispatched, which automatically updates
 *    cart-items-component, cart-icon (badge), and the shipping-progress bar.
 *  • No full drawer re-render — we never touch innerHTML of the drawer root.
 *  • Event-delegated — one listener on document, not one per button.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * NOTE: This file is loaded as a plain <script type="module"> from Shopify CDN
 * (no bundler), so @theme/* aliases are NOT available here. We replicate the
 * tiny helpers inline rather than importing them.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ─── Inline helpers (replicate @theme/utilities fetchConfig & @theme/events) ─

/**
 * Minimal clone of fetchConfig('json', { body }) from @theme/utilities.
 * Returns a POST RequestInit with the correct Content-Type / Accept headers.
 * @param {string} body
 * @returns {RequestInit}
 */
function buildPostConfig(body) {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body,
  };
}

/**
 * Dispatches a cart:update CustomEvent on `document` with the same detail
 * shape produced by CartAddEvent / CartUpdateEvent in @theme/events.
 * cart-icon.js and component-cart-items.js both listen for 'cart:update'.
 *
 * @param {Object} cartData       - Partial or full cart object
 * @param {number} itemCount      - Total item_count after add
 * @param {number} totalPrice     - Cart total_price in cents after add
 * @param {Record<string,string>} sections - Section HTML keyed by section id
 */
function dispatchCartUpdate(cartData, itemCount, totalPrice, sections) {
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cartData,
        sourceId: 'cart-upsell',
        data: {
          source: 'cart-upsell',
          itemCount,
          total_price: totalPrice,
          sections,
        },
      },
    })
  );
}

// ─── Constants ───────────────────────────────────────────────────────────────

/** CSS class applied to an upsell card while it is being added. */
const ADDING_CLASS = 'cart-upsell-item--adding';

/** CSS class applied to an upsell card after it has been successfully added.
 *  The card fades out via CSS transition triggered by this class. */
const ADDED_CLASS = 'cart-upsell-item--added';

/** Delay (ms) before removing the card node from the DOM after fade-out. */
const REMOVE_DELAY_MS = 400;

// ─── Event Delegation ────────────────────────────────────────────────────────

/**
 * Single delegated click listener on the document.
 * Only processes clicks on `.cart-upsell-item__add-btn` elements.
 * @param {MouseEvent} event
 */
document.addEventListener('click', async (event) => {
  const btn = /** @type {HTMLElement} */ (event.target).closest(
    '.cart-upsell-item__add-btn'
  );

  // Bail if the click was not on an upsell add button, or if the button is
  // already in a loading / added state (prevent double-submission).
  if (!btn || btn.disabled) return;

  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  const card = btn.closest('.cart-upsell-item');
  if (!card) return;

  // ── 1. Optimistic UI: lock the button immediately ─────────────────────────
  setButtonLoading(btn, true);

  // ── 2. Fire the API call ──────────────────────────────────────────────────
  try {
    // Resolve all cart-items-component section IDs so the event payload
    // includes section HTML → component-cart-items.js uses morph (no extra fetch).
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.add(el.dataset.sectionId);
      }
    });

    const response = await fetch(
      Theme.routes.cart_add_url,
      buildPostConfig(
        JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }],
          // Request section HTML in one go — avoids a second fetch in the component.
          sections: Array.from(sectionIds).join(',') || undefined,
          sections_url: window.location.pathname,
        })
      )
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.description || `HTTP ${response.status}`);
    }

    // /cart/add.js returns { items: [...], item_count?, total_price?, sections?: {...} }
    const addData = await response.json();

    // item_count and total_price are available directly from the add response —
    // no second GET /cart.js needed.
    const itemCount = addData.item_count ?? addData.items?.reduce((sum, i) => sum + i.quantity, 0) ?? 1;
    const totalPrice = addData.total_price ?? 0;

    // ── 3. Dispatch global cart event ─────────────────────────────────
    // Passes sections HTML so component-cart-items.js morphs in place
    // instead of firing an extra sectionRenderer.renderSection() call.
    dispatchCartUpdate(
      addData,
      itemCount,
      totalPrice,
      addData.sections ?? {}
    );

    // ── 5. Animate the card out ────────────────────────────────────────
    removeUpsellCard(card);
  } catch (error) {
    console.error('[cart-upsell] Failed to add item:', error.message);

    // ── 6. On error: restore button ────────────────────────────────────
    setButtonLoading(btn, false);
    setButtonError(btn);
  }
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Puts a button into loading state (or restores it).
 * Stores the original label in a data attribute so we can restore it.
 *
 * @param {HTMLElement} btn
 * @param {boolean} isLoading
 */
function setButtonLoading(btn, isLoading) {
  if (isLoading) {
    // Store original label for restoration
    btn.dataset.originalLabel = btn.textContent?.trim() ?? 'Add';

    // Show a compact loading spinner via CSS + text
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.innerHTML = `
      <span class="cart-upsell-spinner" aria-hidden="true"></span>
      <span class="visually-hidden">Adding…</span>
    `;
  } else {
    // Restore original state
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
    btn.textContent = btn.dataset.originalLabel ?? 'Add';
  }
}

/**
 * Briefly shows an error label on the button.
 * @param {HTMLElement} btn
 */
function setButtonError(btn) {
  btn.textContent = 'Error — retry';
  btn.classList.add('cart-upsell-item__add-btn--error');

  // Auto-restore after 2s
  setTimeout(() => {
    btn.textContent = btn.dataset.originalLabel ?? 'Add';
    btn.classList.remove('cart-upsell-item__add-btn--error');
  }, 2000);
}

/**
 * Smoothly removes a upsell card from the DOM by:
 * 1. Adding the `--added` CSS class which triggers a CSS opacity + height transition.
 * 2. After the transition, removing the node entirely (prevents stale cards).
 *
 * @param {Element} card
 */
function removeUpsellCard(card) {
  // Lock the width so the adjacent cards don't jump during collapse
  const width = card.getBoundingClientRect().width;
  card.style.minWidth = `${width}px`;
  card.style.maxWidth = `${width}px`;

  card.classList.add(ADDING_CLASS);

  // One rAF to ensure the class is applied before the transition starts
  requestAnimationFrame(() => {
    card.classList.add(ADDED_CLASS);
  });

  // Remove from DOM after transition completes
  setTimeout(() => {
    card.remove();

    // If the upsell row is now empty, hide the entire upsell section
    const grid = document.querySelector('.cart-upsell-grid');
    if (grid && grid.children.length === 0) {
      const wrapper = document.querySelector('.cart-upsell-wrapper');
      wrapper?.remove();
    }
  }, REMOVE_DELAY_MS);
}
